import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ListOfSectionMenu from './ListOfSectionMenu';
import { toggleModal } from '../../reducers/modalReducer';
import { useDispatch } from 'react-redux';
import KeyFeatureSeeMoreModal from '../modals/KeyFeatureSeeMoreModal';
import ModalWrapper from '../common/Modal/ModalWrapper';
import { useNavigate } from 'react-router-dom';

const ModuleCardMenusNew = ({
    module,
    onNavigateSection,
    menus,
    reportMenu,
    company,
    setTab,
    setIsWorkflowReport,
    setCompanyDashboardTab,
}) => {
    const [selectedSeeMore, setSelectedSeeMore] = useState({});
    const [activeDynamicMenu, setActiveDynamicMenu] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const hoverTimeoutRef = useRef(null);
    const dispatch = useDispatch();
    let sections = module?.['sections'] || module?.['module']?.['sections'] || [];

    const isExpansionLocked =
        ['warranty-disclosure', 'directors-questionnaire'].includes(module?.type) &&
        !company?.expensions?.[module?.id];

    const isPaid = module?.isPaid || false;
    const hasBoughtExpension = Array.isArray(company?.boughtExpensions) &&
        company.boughtExpensions.some(id => id?.toString() === module?._id?.toString());
    const hasPurchased = !isPaid || module?.hasPurchased === true || hasBoughtExpension;

    /** Align with SidebarIndexNew / company stats: module completion drives report & workflow menus. */
    const moduleCompletionPercent =
        Number(module?.stats?.completionPercent ?? module?.completenessPercent ?? 0) || 0;
    const canUseReportMenus = moduleCompletionPercent > 0;

    const openPaidModulePurchaseModal = () => {
        dispatch(toggleModal({
            open: 'subscriptionPaymentConfirm',
            purchasePlan: 'tempModulePurchase',
            purchaseDefaultStep: 'tempModulePurchase',
            modulePurchase: {
                tempModuleId: module?._id,
                moduleName: module?.moduleName,
                price: module?.price,
                subscriptionTier: company?.subscription,
                companyObjective: company?.testType,
                stripeProductPriceId: module?.stripeProductPriceId,
            },
        }));
    };

    const isReportOrWorkflowMenu = (menu) =>
        menu?.key === 'report' ||
        menu?.key === 'workflowReport' ||
        menu?.label?.toLowerCase?.() === 'report';

    const handleDynamicMenuClick = (menu) => {
        if (!menu || menu.isActive === false || isExpansionLocked) return;
        if (isReportOrWorkflowMenu(menu)) {
            if (isPaid && !hasPurchased) {
                openPaidModulePurchaseModal();
                return;
            }
            if (!canUseReportMenus) return;
            if (menu?.key === 'workflowReport') {
                setCompanyDashboardTab?.('Dashboard');
                setIsWorkflowReport?.(true);
            }
            if (module?.['moduleName'] === "Business Audit") {
                setTab(`${module['slug']}-${company['testType']}-report`);
                navigate(`/company-old/${company['_id']}#${module['slug']}-${company['testType']}-report`);
            } else {
                setTab(`${module['slug']}-report`);
                navigate(`/company-old/${company['_id']}#${module['slug']}-report`);
            }
            return;
        };

        if (menu.actionType === 'navigate') {
            if (menu.navigateUrl) {
                window.open(menu.navigateUrl, '_blank', 'noopener,noreferrer');
            }
            return;
        }

        if (menu.actionType === 'modal') {
            setActiveDynamicMenu(menu);
            dispatch(toggleModal({ open: 'dynamicModuleMenuModal' }));
        }
    };

    const dynamicMenus = Array.isArray(module?.menusConfig) ? module.menusConfig.filter(m => m && m.isActive !== false) : [];

    const openMenu = useCallback(() => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const SUB_WIDTH = 240;
            // Rough height estimates used only for overflow detection
            const DROPDOWN_EST_HEIGHT = 300;
            const SUB_EST_HEIGHT = 400;

            // Flip main dropdown above the trigger if not enough space below
            const spaceBelow = window.innerHeight - rect.bottom;
            const flipDown = spaceBelow < DROPDOWN_EST_HEIGHT;

            // Flip sub-menu leftward if it would overflow the right edge
            const flipSub = (rect.right + SUB_WIDTH) > window.innerWidth;

            // Flip sub-menu upward if it would overflow the bottom of the viewport.
            // When the main dropdown is flipped upward the sub starts near the top
            // of the dropdown; when it is below the trigger we check from rect.bottom.
            const subOriginY = flipDown ? rect.top - DROPDOWN_EST_HEIGHT : rect.bottom;
            const flipSubDown = (subOriginY + SUB_EST_HEIGHT) > window.innerHeight;

            setDropdownPos({
                top: flipDown
                    ? rect.top + window.scrollY - 4
                    : rect.bottom + window.scrollY + 4,
                left: rect.right + window.scrollX,
                flipDown,
                flipSub,
                flipSubDown,
            });
        }
        setIsOpen(true);
    }, []);

    const closeMenu = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => setIsOpen(false), 400);
    }, []);

    // Close on scroll / resize so the portal doesn't drift.
    // Ignore scroll events that originate inside the dropdown itself (e.g. the sections list).
    useEffect(() => {
        if (!isOpen) return;
        const close = (e) => {
            if (e.target && e.target.closest && e.target.closest('.ds-module-menu__dropdown')) return;
            setIsOpen(false);
        };
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [isOpen]);

    // The dropdown is portalled to document.body so it escapes every
    // overflow:hidden / clipping ancestor (the 200px card, the rounded dashboard card, etc.)
    const dropdown = isOpen ? ReactDOM.createPortal(
        <div
            className={[
                'ds-module-menu__dropdown',
                dropdownPos.flipSub ? 'ds-module-menu__dropdown--flip-sub' : '',
                dropdownPos.flipDown ? 'ds-module-menu__dropdown--flip-down' : '',
                dropdownPos.flipSubDown ? 'ds-module-menu__dropdown--flip-sub-down' : '',
            ].filter(Boolean).join(' ')}
            style={{
                position: 'absolute',
                top: dropdownPos.top,
                left: dropdownPos.left,
                // Flip upward: translateY(-100%) moves the bottom edge to the trigger's top.
                // Flip downward (default): translateY(0) keeps the top edge at the trigger's bottom.
                transform: dropdownPos.flipDown
                    ? 'translateX(-100%) translateY(-100%)'
                    : 'translateX(-100%) translateY(0)',
                zIndex: 99999,
                // Override the CSS initial state (opacity:0, pointer-events:none) since
                // the portal lives in document.body and the :hover selector no longer applies
                opacity: 1,
                pointerEvents: 'auto',
            }}
            onMouseEnter={openMenu}
            onMouseLeave={closeMenu}
        >
            <div className="ds-module-menu__section ds-submenu">
                {sections.length > 0 && (menus?.includes('listOfSections') || module?.['menus']?.includes('listOfSections')) && (
                    <ListOfSectionMenu module={module} sections={sections} onNavigateSection={onNavigateSection} company={company} />
                )}
                {dynamicMenus.map((menu) => (
                    <button
                        key={menu.key || menu.label}
                        type="button"
                        onClick={() => handleDynamicMenuClick(menu)}
                        className="ds-module-menu__item df aic"
                    >
                        {menu.iconURL && (
                            <img
                                style={{ width: '16px', height: '16px', marginRight: '8px' }}
                                src={menu.iconURL}
                                alt={menu.label || 'menu-icon'}
                            />
                        )}
                        {menu.label || 'Menu'}
                    </button>
                ))}

                {reportMenu && reportMenu.length > 0 && (
                    <>
                        <div className="ds-module-menu__divider" />
                        {reportMenu.map((menu) => (
                            <button
                                key={menu.key || menu.label}
                                type="button"
                                disabled={!canUseReportMenus && (menu?.key === 'report' || menu?.key === 'workflowReport')}
                                onClick={() => handleDynamicMenuClick(menu)}
                                className={[
                                    'ds-module-menu__item df aic',
                                    !canUseReportMenus && (menu?.key === 'report' || menu?.key === 'workflowReport') ? 'op20 pen' : '',
                                ].filter(Boolean).join(' ')}
                            >
                                {menu.key === 'report' && (
                                    <img style={{ width: '16px', height: '16px', marginRight: '8px' }} src={require('../../../images/dashboard/icons/R.png')} alt="report" />
                                )}
                                {menu.key === 'workflowReport' && (
                                    <img style={{ width: '16px', height: '16px', marginRight: '8px' }} src={require('../../../images/dashboard/icons/WF.png')} alt="workflow-report" />
                                )}
                                {menu.label || 'Menu'}
                            </button>
                        ))}
                    </>
                )}
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <>
            <div
                className="ds-module-menu"
                onMouseEnter={openMenu}
                onMouseLeave={closeMenu}
            >
                <div ref={triggerRef} className="ds-module-card__menu">•••</div>
                {dropdown}
            </div>
            {Object.keys(selectedSeeMore).length ? <KeyFeatureSeeMoreModal selectedSeeMore={selectedSeeMore} /> : null}

            {activeDynamicMenu && activeDynamicMenu.actionType === 'modal' && (
                <ModalWrapper
                    modalType="dynamicModuleMenuModal"
                    modalCardClasses="maw900px"
                    onCloseHandler={() => setActiveDynamicMenu(null)}
                    customStyle={{ padding: '2rem' }}
                >
                    <div className="poppins-font-style w100 df fdc">
                        {activeDynamicMenu.modalTitle && (
                            <h2 className="fs1-5rem fw600 c15 mb1rem tac">
                                {activeDynamicMenu.modalTitle}
                            </h2>
                        )}
                        {activeDynamicMenu.modalImageURL && (
                            <img
                                src={activeDynamicMenu.modalImageURL}
                                alt={activeDynamicMenu.modalTitle || 'menu-modal'}
                                style={{ width: '100%', borderRadius: '12px', objectFit: 'contain' }}
                            />
                        )}
                    </div>
                </ModalWrapper>
            )}
        </>
    )
};

export default ModuleCardMenusNew;
